import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";

type Screen = "main" | "google" | "github" | "admin_creds" | "admin_mfa";

export function useAuthFlow() {
  const { 
    user, 
    isAuthModalOpen, 
    setAuthModalOpen, 
    loginPasswordless,
    validateAdminPassword,
    loginAdmin 
  } = useAuth();

  const [screen, setScreen] = useState<Screen>("main");
  const [googleEmail, setGoogleEmail] = useState("");
  const [githubUsername, setGithubUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyStep, setPasskeyStep] = useState<"idle" | "prompt" | "scanning" | "success">("idle");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  
  const mfaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (screen === "admin_mfa" && !qrCodeUrl) {
      fetch("/api/auth/qr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminPassword }),
      })
        .then(res => res.json())
        .then(data => setQrCodeUrl(`data:image/png;base64,${data.qr_base64}`))
        .catch(e => console.error("Error fetching QR code", e));
    }
  }, [screen, qrCodeUrl, adminPassword]);

  useEffect(() => {
    if (isAuthModalOpen) {
      setScreen("main");
      setGoogleEmail("");
      setGithubUsername("");
      setAdminPassword("");
      setMfaCode("");
      setError(null);
      setLoading(false);
      setPasskeyStep("idle");
    }
  }, [isAuthModalOpen]);

  useEffect(() => {
    if (screen === "admin_mfa") {
      setTimeout(() => mfaInputRef.current?.focus(), 150);
    }
  }, [screen]);

  const handleGoogleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleEmail.trim()) return;
    setError(null);
    setPasskeyStep("prompt");
  };

  const startGooglePasskeyScan = async () => {
    setPasskeyStep("scanning");
    try {
      await loginPasswordless("google", googleEmail.trim());
      setPasskeyStep("success");
      setTimeout(() => {
        setAuthModalOpen(false);
      }, 1000);
    } catch (err) {
      setError("Passkey authentication failed");
      setPasskeyStep("idle");
    }
  };

  const handleGithubSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!githubUsername.trim()) return;
    setError(null);
    setPasskeyStep("prompt");
  };

  const startGithubPasskeyScan = async () => {
    setPasskeyStep("scanning");
    try {
      await loginPasswordless("github", githubUsername.trim());
      setPasskeyStep("success");
      setTimeout(() => {
        setAuthModalOpen(false);
      }, 1000);
    } catch (err) {
      setError("FIDO2 Security Key validation failed");
      setPasskeyStep("idle");
    }
  };

  const handleAdminCredsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await validateAdminPassword(adminPassword);
      if (res.success) {
        setScreen("admin_mfa");
      } else {
        setError(res.error || "Invalid administrative password");
      }
    } catch (err) {
      setError("Authentication service error. Please try again.");
    }
  };

  const handleAdminMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const res = await loginAdmin(adminPassword, mfaCode);
      if (res.success) {
        setScreen("main");
        setAuthModalOpen(false);
      } else {
        setError(res.error || "Authentication failed");
        setMfaCode("");
        mfaInputRef.current?.focus();
      }
    } catch (err) {
      setError("MFA system error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setMfaCode(val);
    if (val.length === 6) {
      setTimeout(() => {
        const form = e.target.form;
        if (form) {
          form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        }
      }, 100);
    }
  };

  return {
    isAuthModalOpen,
    setAuthModalOpen,
    screen,
    setScreen,
    googleEmail,
    setGoogleEmail,
    githubUsername,
    setGithubUsername,
    adminPassword,
    setAdminPassword,
    mfaCode,
    setMfaCode,
    loading,
    error,
    setError,
    passkeyStep,
    setPasskeyStep,
    qrCodeUrl,
    mfaInputRef,
    handleGoogleSubmit,
    startGooglePasskeyScan,
    handleGithubSubmit,
    startGithubPasskeyScan,
    handleAdminCredsSubmit,
    handleAdminMfaSubmit,
    handleMfaChange
  };
}
